import React, { useCallback, useEffect, useState } from 'react';
import { MetaTags } from 'react-meta-tags';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Container, Row } from 'reactstrap';
import { Form } from 'react-bootstrap';
import toast, { Toaster } from 'react-hot-toast';
import { DashBreadcrumb } from '../../components/Common/Breadcrumb';
import CsLineIcons from '../../cs-line-icons/CsLineIcons';
import DHPagination from '../../components/Pagination';
import { fetchCodReturnOrders, approveCodReturnOrder } from './actions/action';
import CodReturnOrdersList from './components/CodReturnOrdersList';

const toastError = {
  style: {
    border: '1px solid #fff',
    padding: '20px',
    color: '#ffffff',
    background: '#f44336',
  },
  iconTheme: { primary: '#FFFAEE', secondary: '#f44336' },
};

const toastSuccess = {
  style: {
    border: '1px solid #fff',
    padding: '20px',
    color: '#ffffff',
    background: '#4CAF50',
  },
  iconTheme: { primary: '#FFFAEE', secondary: '#4CAF50' },
};

const CodReturnOrders = () => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [approvingId, setApprovingId] = useState(null);

  const currentCountry = localStorage.getItem('WEBSITE_COUNTRY');
  const country = (currentCountry || '').toLowerCase() === 'all' ? '' : currentCountry;

  const {
    list = [],
    totalRecords = 0,
    totalPages: apiTotalPages = 0,
    isLoading,
    listError,
  } = useSelector((state) => state.codReturnOrders) || {};

  const loadList = useCallback(
    (currentPage, currentPerPage, searchValue) => {
      fetchCodReturnOrders({
        params: {
          // both shapes so the API can read whichever it expects
          page: currentPage,
          per_page: currentPerPage,
          start: (currentPage - 1) * currentPerPage,
          limit: currentPerPage,
          search_qry: searchValue,
          country,
          orderBy: 'id',
        },
        dispatch,
      }).catch(() => {});
    },
    [dispatch, country],
  );

  useEffect(() => {
    const delay = setTimeout(() => loadList(page, perPage, search), 300);
    return () => clearTimeout(delay);
  }, [page, perPage, search, loadList]);

  // approve sends is_refund 1, reject sends 2
  const handleDecision = async (row, action) => {
    const rowId = row?.queue_id || row?.id;
    const rejected = action === 'reject';
    try {
      setApprovingId(rowId);
      const response = await approveCodReturnOrder({
        params: { queue_id: rowId, is_refund: rejected ? 2 : 1 },
        dispatch,
      });
      if (response?.code === 100 || response?.code === 200) {
        toast.success(
          response?.message || (rejected ? 'Marked as cash not received.' : 'Marked as cash received.'),
          toastSuccess,
        );
        loadList(page, perPage, search);
      } else {
        toast.error(
          response?.message ||
            (rejected ? 'Failed to mark as cash not received.' : 'Failed to mark as cash received.'),
          toastError,
        );
      }
    } catch (error) {
      toast.error('Failed to save the decision.', toastError);
    } finally {
      setApprovingId(null);
    }
  };

  const startIndex = (page - 1) * perPage;
  const totalPages = Math.max(
    1,
    apiTotalPages,
    Math.ceil((totalRecords || 0) / perPage),
    list?.length === perPage ? page + 1 : page,
  );

  return (
    <React.Fragment>
      <div className="page-content codReturnOrders">
        <MetaTags>
          <title>COD Return Orders</title>
        </MetaTags>
        <Container fluid>
          <Row>
            <Col>
              <DashBreadcrumb title="Orders" breadcrumbItem="COD Return Orders" />
            </Col>
            <Col xl={3} md={6} className="mb-3">
              <div className="searchBox">
                <div className="d-inline-block float-md-start me-1 mb-1 search-input-container w-100 bg-foreground">
                  <Form.Control
                    type="text"
                    placeholder="Order no / return reference"
                    value={search}
                    id="listSearch"
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                  <span className="search-magnifier-icon searchIcon">
                    <CsLineIcons icon="search" />
                  </span>
                </div>
              </div>
            </Col>
          </Row>

          <div className="dt-table">
            <CodReturnOrdersList
              rows={list}
              isLoading={isLoading}
              error={listError}
              startIndex={startIndex}
              onDecision={handleDecision}
              pendingId={approvingId}
            />
            {list?.length > 0 ? (
              <DHPagination
                totalPages={totalPages}
                currentPage={page - 1}
                setPaginationValue={(value) => setPage(Number(value.page) + 1)}
                setLimitValue={(value) => {
                  setPerPage(Number(value.limit));
                  setPage(1);
                }}
              />
            ) : null}
          </div>
        </Container>
      </div>
      <Toaster position="top-right" reverseOrder={false} />
    </React.Fragment>
  );
};

export default CodReturnOrders;
